/**
 * Compact Settings card for the personal energy model: samples, last training time, holdout
 * improvement per axis, whether the planner is using its forecast, and a "Retrain now" action.
 * Training runs in the dedicated Worker; this card only reads status and starts a manual run.
 *
 * @module apps/web/src/components/settings/EnergyModelCard
 */
import { useCallback, useEffect, useState } from 'react';
import { Activity, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { EnergyHeatmap } from './EnergyHeatmap';
import { useUserStore } from '@/stores/userStore';
import {
  energyTrainingRunner,
  fetchEnergyModelStatus,
  type EnergyModelStatus,
} from '@/lib/energyModel';

/** Axis labels in metric order. */
const AXES = ['Productivity', 'Rejuvenation', 'Resistance'] as const;

/** Format a relative improvement as a signed percentage, or a dash when unavailable. */
export function formatImprovement(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  const percent = Math.round(value * 1000) / 10;
  return `${percent > 0 ? '+' : ''}${percent}%`;
}

/** Show energy-model status and let the person retrain on demand. */
export function EnergyModelCard() {
  const userId = useUserStore(state => state.userId);
  const [status, setStatus] = useState<EnergyModelStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /** Reload status from the API. */
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setStatus(await fetchEnergyModelStatus());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** Start a manual run (ignores freshness and device checks) and refresh afterwards. */
  const retrain = async () => {
    if (!userId) return;
    const runner = energyTrainingRunner(userId);
    runner.onProgress = (step, total) => setProgress(`Training ${step}/${total}`);
    setError(null);
    setProgress('Preparing data');
    try {
      await runner.run({ force: true });
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Training failed.');
    } finally {
      runner.onProgress = undefined;
      setProgress(null);
    }
  };

  const latest = status?.latest;
  const metrics = latest?.metrics;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5" />
          Energy model
        </CardTitle>
        <CardDescription>
          A small model trained privately in this browser on your check-ins. The planner uses it
          only when it beats your check-in profile on recent data.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && !status ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : !latest ? (
          <p className="text-sm text-muted-foreground">
            No model yet. It trains automatically when your device is idle.
          </p>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={latest.promoted ? 'default' : 'secondary'}>
                {latest.promoted ? 'In use by the planner' : 'Not in use'}
              </Badge>
              {status?.forecast && (
                <Badge variant="outline">
                  {status.forecast.fresh ? 'Forecast current' : 'Forecast expired'}
                </Badge>
              )}
            </div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
              <dt className="text-muted-foreground">Samples</dt>
              <dd>{latest.sampleCount}</dd>
              <dt className="text-muted-foreground">Last trained</dt>
              <dd>{new Date(latest.createdAt || latest.trainedAt).toLocaleString()}</dd>
            </dl>
            {metrics && (
              <div className="space-y-1">
                <div className="text-muted-foreground">
                  Holdout improvement over your profile ({metrics.testSamples} recent check-ins)
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {AXES.map((axis, index) => (
                    <div key={axis} className="rounded-md border bg-background p-2">
                      <div className="text-xs text-muted-foreground">{axis}</div>
                      <div className="font-medium">
                        {formatImprovement(metrics.improvement?.[index])}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">{metrics.reason}</p>
              </div>
            )}
          </div>
        )}
        <EnergyHeatmap heatmap={status?.heatmap} />
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={retrain} disabled={!userId || Boolean(progress)}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Retrain now
          </Button>
          {progress && <span className="text-sm text-muted-foreground">{progress}</span>}
          {error && <span className="text-sm text-destructive">{error}</span>}
        </div>
      </CardContent>
    </Card>
  );
}
